import React from "react";
import { format, parseISO } from "date-fns";
import { Check, X } from "lucide-react";
import { buildOrderStatusSteps, colorForStep } from "../../Lib/orderStatusSteps";

const Dot = ({ state, color, title, showCheck }) => {
  if (state === "current") {
    return (
      <span title={title} className='relative flex h-2.5 w-2.5 shrink-0'>
        <span
          className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${color}`}
        />
        <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${color}`} />
      </span>
    );
  }
  if (state === "reached") {
    return (
      <span
        title={title}
        className={`flex h-2.5 w-2.5 shrink-0 items-center justify-center rounded-full ${color}`}
      >
        {showCheck && <Check className='h-2 w-2 text-white' strokeWidth={4} />}
      </span>
    );
  }
  if (state === "terminal") {
    return (
      <span
        title={title}
        className='flex h-2.5 w-2.5 shrink-0 items-center justify-center rounded-full bg-[#590C07]'
      >
        <X className='h-2 w-2 text-white' strokeWidth={4} />
      </span>
    );
  }
  return <span title={title} className='h-2.5 w-2.5 shrink-0 rounded-full bg-gray-300' />;
};

const Connector = ({ color }) => (
  <span
    className={`h-0.5 flex-1 min-w-[10px] rounded-full transition-all duration-500 ${
      color || "bg-gray-200"
    }`}
  />
);

const stepLabel = (name) => name.replace(/_/g, " ");
const formatTime = (time) => (time ? format(parseISO(time), "p, MMM d") : null);

const describeStep = (step, { terminal = false } = {}) => {
  const label = stepLabel(step.name);
  const time = formatTime(step.time ?? step.createdAt);
  if (terminal) return time ? `${label} — ${time}` : label;
  if (step.isCurrent) return time ? `${label} (current) — ${time}` : `${label} (current)`;
  if (step.reached) return time ? `${label} — ${time}` : `${label} — done`;
  return `${label} — upcoming`;
};

/**
 * Compact animated status track for a table row — pure step/connector
 * visualization with a descriptive hover tooltip per step (timestamps and
 * full deltas live in RiderStatusTimeline).
 */
const OrderProgressTrack = ({ statuses, currentStatus }) => {
  const { isTerminalAlt, steps } = buildOrderStatusSteps(statuses, currentStatus);

  if (isTerminalAlt) {
    if (steps.length === 0) {
      return <span className='h-2.5 w-2.5 rounded-full bg-gray-300' />;
    }
    return (
      <div className='flex items-center gap-1 min-w-[80px]'>
        {steps.map((step, i) => {
          const isLast = i === steps.length - 1;
          return (
            <React.Fragment key={step.id ?? step.name}>
              {i > 0 && (
                <Connector color={isLast ? "bg-[#590C07]" : colorForStep(step.name)} />
              )}
              <Dot
                state={isLast ? "terminal" : "reached"}
                color={colorForStep(step.name)}
                title={describeStep(step, { terminal: isLast })}
              />
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  return (
    <div className='flex items-center gap-1 min-w-[100px]'>
      {steps.map((step, i) => (
        <React.Fragment key={step.name}>
          {i > 0 && (
            <Connector
              color={steps[i - 1].reached && step.reached ? colorForStep(step.name) : undefined}
            />
          )}
          <Dot
            state={step.isCurrent ? "current" : step.reached ? "reached" : "upcoming"}
            color={colorForStep(step.name)}
            showCheck={step.reached && !step.isCurrent && step.name === "delivered"}
            title={describeStep(step)}
          />
        </React.Fragment>
      ))}
    </div>
  );
};

export default OrderProgressTrack;
